import { api } from '../core/network/api';
import { ACTION_ID_HEADER } from './action/dispatch';
import type { DisplayCurrency, FrozenFiat, Money } from './money';

// Re-exported so a screen can import its money types from the same module as the
// call that returns them.
export type { DisplayCurrency, FrozenFiat, Money };

/**
 * Stamps a gesture's actionId onto a mutating request, so the server can tell a
 * replay from a fresh action. Mutations take `actionId` as their last argument;
 * it is optional only so read paths and tests stay terse — dispatchAction()
 * always supplies one.
 */
const act = (actionId?: string) => (actionId ? { headers: { [ACTION_ID_HEADER]: actionId } } : undefined);

// ─── Clock ───────────────────────────────────────────────────────────────────
export interface ClockNow {
  dayNumber: number;
  phase: 'day' | 'dusk' | 'night' | 'dawn';
  msIntoPhase: number;
  phaseMs: number;
  msToNextPhase: number;
  dayProgress: number;
  isDark: boolean;
  serverNow: number;
  epochMs: number;
  dayMs: number;
  phases: { name: 'day' | 'dusk' | 'night' | 'dawn'; ms: number }[];
  nightNumber: number;
  spawnMultiplier: number;
}
export const clockApi = {
  now: () => api.get<ClockNow>('/clock'),
};

// ─── Health ──────────────────────────────────────────────────────────────────
export interface EnergyStatus {
  energy: number; maxEnergy: number;
  refills: number; maxRefills: number;
  /**
   * ms until the next rest charge lands; 0 when the set is full.
   *
   * Optional because an older server does not send it — the HUD then shows the
   * circles with no timer, which is what it always did.
   */
  nextRefillMs?: number;
  lockedOut: boolean; lockedOutMs: number;
  extensionCostShrooms: number;
  /** The hero's own home (a finished Hut), or null while he has none. */
  home: Tile | null;
  /** Would the server accept a sleep right now? At home always; otherwise night only. */
  canSleep: boolean;
  /**
   * He is inside the house sleeping (field.config SLEEP_DURATION_MS) — the farm
   * refuses work until it ends. All four optional: an older server sends none of
   * them, and `sleeping` absent must read as awake, never as a locked farm.
   */
  sleeping?: boolean;
  sleepEndsInMs?: number;
  /** Shrooms to skip the remainder — falls as the clock runs down, so re-read it. */
  sleepSkipCostShrooms?: number;
  sleepDurationMs?: number;
}
export interface Tile { tx: number; ty: number }
export interface Health {
  hp: number; maxHp: number; inCombat: boolean; low: boolean; dead: boolean;
  tent: Tile | null;
  home: Tile | null;
  /**
   * Server's answer to "where would I wake up": home → tent → origin.
   * `source` says WHICH — 'origin' means the player has no camp at all, which is
   * not the same as a camp that happens to sit at (0,0). Optional so an older
   * server's payload still type-checks.
   */
  respawnAt: Tile & { source?: RespawnSource };
  /** Which of the three tent states the server sees (absent on older servers). */
  tentState?: TentState;
}
/** never = no client ever reported a position; packed = the player collected it. */
export type TentState = 'pitched' | 'packed' | 'never';
export type RespawnSource = 'home' | 'tent' | 'camp' | 'origin';
export interface Grave {
  id: string; tx: number; ty: number; coins: number;
  resource: string | null; amount: number; expiresInMs: number;
}

// ─── Drops (items on the ground) ─────────────────────────────────────────────
import type { GroundDropData } from './map/drops';
export type GroundDrop = GroundDropData;
/**
 * Shown whenever a collect stops early on a full bag. Kept as one constant because
 * three panels (ground pickup, cooking, husbandry) report the same state, and it
 * mirrors BAG_FULL_MESSAGE in backend/src/game/common/bag-full.ts — the flag comes
 * over the wire, the wording is ours.
 */
export const BAG_FULL_MESSAGE = 'Your bag is full — make room and it will still be there';

export const dropsApi = {
  list: () => api.get<{ ttlMs: number; drops: GroundDrop[] }>('/drops'),
  claim: (id: string, actionId?: string) => api.post<{ claimed: boolean; itemId: string; quantity: number }>(`/drops/${id}/claim`, undefined, act(actionId)),
  claimMany: (ids: string[], actionId?: string) =>
    // `bagFull` reports a sweep that stopped early — `collected` is still banked.
    api.post<{ collected: { id: string; itemId: string; quantity: number }[]; bagFull: boolean }>('/drops/claim-many', { ids }, act(actionId)),
  claimAll: (actionId?: string) => api.post<{ collected: { itemId: string; quantity: number }[]; bagFull: boolean }>('/drops/claim-all', undefined, act(actionId)),
};

// ─── Horse (taming + riding) ─────────────────────────────────────────────────
export interface HorseTypeDto {
  id: 'work' | 'racing' | 'heavy'; name: string; speed: number; bagSlots: number; fearless: boolean;
}
export interface HorseStatus {
  types: HorseTypeDto[];
  tame: { baseChance: number; perFeed: number; maxChance: number; feedItems: readonly string[] };
  horse: 'work' | 'racing' | 'heavy' | null;
}
export const horseApi = {
  status: () => api.get<HorseStatus>('/horse'),
  tame: (type: string, feeds: number, actionId?: string) =>
    api.post<{ success: boolean; type: string | null; feedsUsed: number; chance: number }>('/horse/tame', { type, feeds }, act(actionId)),
  release: (actionId?: string) => api.post<{ released: string }>('/horse/release', undefined, act(actionId)),
};

// ─── Food (eating + cooking) ─────────────────────────────────────────────────
export interface PoisonDto { hpPerSec: number; durationSec: number; }
export interface CookingSlot { id: string; cookedItem: string; readyInMs: number; ready: boolean; }
export interface FoodStatus {
  poison: PoisonDto;
  cookSecondsPerUnit: number;
  campfireCost: { wood: number; stone: number };
  campfire: { tx: number; ty: number } | null;
  cooking: CookingSlot[];
}
export interface EatResult {
  ate: string; healed: number; energy: number;
  poisoned: boolean; poison: PoisonDto | null;
}
export const foodApi = {
  status: () => api.get<FoodStatus>('/food'),
  eat: (itemId: string, actionId?: string) =>
    api.post<EatResult>('/food/eat', { itemId }, act(actionId)),
  cook: (itemId: string, quantity: number, actionId?: string) =>
    api.post<FoodStatus>('/food/cook', { itemId, quantity }, act(actionId)),
  collect: (actionId?: string) =>
    api.post<{ collected: Record<string, number>; bagFull: boolean }>('/food/collect', undefined, act(actionId)),
  buildCampfire: (tx: number, ty: number, actionId?: string) =>
    api.post<FoodStatus>('/food/campfire', { tx, ty }, act(actionId)),
};

// ─── Mobs (night hostiles) ───────────────────────────────────────────────────
export interface MobStatDto {
  id: string; name: string; hp: number; damage: number; speed: number;
  aggroTiles: number; behaviour: 'melee' | 'ranged' | 'dasher' | 'kiter' | 'chief';
  biome?: 'meadow' | 'volcano';
  ranged?: { range: number; everyMs: number; damage: number };
  dash?: { tiles: number; everyMs: number };
  callsAdds?: number;
}
export interface MobsStatus {
  mobs: MobStatDto[];
  ai: {
    litZoneTiles: number; spawnMinTiles: number; spawnMaxTiles: number;
    baseSpawnRate: number; spawnEveryMs: number; deAggroTiles: number;
    loseTargetMs: number; dissolveMs: number; maxAlive: number;
  };
  nightNumber: number;
  spawnRate: number;
}
export interface MobKillResult {
  mob: string;
  dropped: (GroundDrop & { name: string })[];
  xp: number; level: number; leveledUp: boolean;
}
export const mobsApi = {
  status: () => api.get<MobsStatus>('/mobs'),
  kill: (mob: string, at?: { tx: number; ty: number }, actionId?: string) =>
    api.post<MobKillResult>('/mobs/kill', { mob, ...at }, act(actionId)),
};

// ─── Fauna ───────────────────────────────────────────────────────────────────
export interface SpeciesStat {
  id: 'chicken' | 'duck' | 'swan' | 'sheep' | 'cow' | 'horse';
  name: string;
  hp: number;
  behaviour: 'wander' | 'graze' | 'flee' | 'skittish' | 'retaliate';
  killable: boolean;
  retaliateDamage?: number;
  xp: number;
}
export interface FaunaStatus {
  species: SpeciesStat[];
  ai: {
    spookRadius: number; skittishRadius: number; fleeTiles: number; calmMs: number;
    wander: { pauseMsMin: number; pauseMsMax: number; stepTiles: number };
    graze: { pauseMsMin: number; pauseMsMax: number; stepTiles: number };
    respawnMinDistance: number;
  };
}
export interface KillResult {
  species: string;
  variant: string | null;
  dropped: (GroundDrop & { name: string })[];
  xp: number; level: number; leveledUp: boolean;
}
export const faunaApi = {
  status: () => api.get<FaunaStatus>('/fauna'),
  kill: (species: string, variant?: string, at?: { tx: number; ty: number }, actionId?: string) =>
    api.post<KillResult>('/fauna/kill', { species, variant, ...at }, act(actionId)),
};

// ─── Field ───────────────────────────────────────────────────────────────────
// Block 8: a plot is anchored to a world TILE (tx,ty), created on demand when the
// player hoes a cleared tile — there is no fixed grid or plot index any more.
export interface Plot {
  tx: number;
  ty: number;
  state: string;   // TILLED | PLANTED
  cropId: string | null;
  watered: boolean;
  ready: boolean;
  remainingMs: number;
  plantedAt: string | null;
  decayStage?: number;   // 0 fresh .. 1 about to revert to grass (empty untended bed)
  rushCost?: number;     // shrooms to ripen this plot now (0 = nothing to buy)
}
export interface Building {
  kind: string;    // torch | fence | …
  tx: number;
  ty: number;
  /** Timed construction: false while scaffolding is up. Absent on older servers = built. */
  isReady?: boolean;
  readyAt?: string | null;
  remainingMs?: number;
  rushCost?: number;   // shrooms to finish it now (0 when finished)
}
export interface Farm {
  energy: number;
  maxEnergy: number;
  energyRegenPerMin: number;
  /**
   * The server's own per-action energy prices (field.config ENERGY_COST), so the
   * client can refuse an unaffordable action BEFORE it starts instead of letting
   * the player watch a progress ring and be refused at the end. It is a MIRROR —
   * never a second copy that can drift — and it is optional: an older server that
   * does not send it leaves the cost unknown, and the client then lets the server
   * decide exactly as before (see views/farm/resources.ts checkEnergy).
   */
  energyCosts?: Partial<Record<'clear' | 'till' | 'plant' | 'water' | 'harvest' | 'swing', number>>;
  plotCount: number;
  maxPlots: number;
  buildingCount: number;
  maxBuildings: number;
  /** The other two placement budgets (absent on older servers → client doesn't gate). */
  fenceCount?: number;
  maxFences?: number;
  decorCount?: number;
  maxDecor?: number;
  plots: Plot[];
  buildings: Building[];
  harvests?: HarvestState[];
  /** The furnace's load, carried on the farm snapshot so a placed furnace can draw
   *  cold vs burning without a second request every repaint (null = cold). */
  smelting?: { type: string; burning: boolean; ready: number; remainingMs: number } | null;
  /**
   * The tent, carried on the snapshot the client loads first. `state: 'never'`
   * means the server has no tile for it at all — the client is the only thing
   * that knows where the camp is, so it is the only thing that can fix that.
   * Absent on older servers.
   */
  tent?: { state: TentState; pitched: boolean; everPitched: boolean; tx: number | null; ty: number | null; safeRadius: number };
  /**
   * Acorns the player has in the ground. Rides on the farm snapshot for the same
   * reason `harvests` does: the map is generated from the SEED, which knows nothing
   * about a player, so a planted tree that is not in this payload does not exist
   * after a reload. Absent on older servers → no saplings, not a crash.
   */
  saplings?: Sapling[];
}
/**
 * One planted acorn. `grown` and `remainingMs` are computed by the server on READ
 * (saplingView) — there is no grown column and no cron — so they are a snapshot of
 * the moment the payload was built, not a fact to cache. Anything long-lived on the
 * client must be derived from `readyAt`.
 */
export interface Sapling {
  tx: number; ty: number; plantedAt: string; readyAt: string;
  grown: boolean; remainingMs: number; size: 'small' | 'big';
}
/** A felled tree / mined rock / gathered prop that's still spent (server-persisted). */
export interface HarvestState { tx: number; ty: number; kind: string; regrowUntil: string; cleared: boolean }
export interface Crop {
  id: string;
  name: string;
  seedCost: number;
  kind: 'crop' | 'bush' | 'tree';
  yield: number;
  growthMs: number;
  xp: number;
  unlockLevel: number;
}
// ─── Dungeon (Block 6) ───────────────────────────────────────────────────────
export interface DungeonDepth { ore: number; gemChance: number; xp: number; mobHpScale: number; }
export interface DungeonConfig {
  depths: number;
  /** Mob ids the server will accept a dungeon kill for (absent on older servers). */
  mobs?: string[];
  hitsPerVein: number;
  energyCostMine: number;
  byDepth: Record<string, DungeonDepth>;
}
export interface DungeonMineResult {
  depth: number; oreDropped: number; gemDropped: number;
  energy: number; maxEnergy: number; xpEarned: number; xp: number; level: number; leveledUp: boolean;
}
/**
 * A cave kill. Unlike a field kill the loot is BANKED, not dropped: the dungeon is
 * its own scene with its own tile space and no ground-drop layer, so `dropped`
 * carries no id/position — it is already in the bag by the time this returns.
 */
export interface DungeonKillResult {
  depth: number;
  mob: string;
  dropped: { itemId: string; quantity: number; name: string }[];
  xp: number; level: number; leveledUp: boolean;
}
export const dungeonApi = {
  config: () => api.get<DungeonConfig>('/dungeon'),
  mine: (depth: number, actionId?: string) => api.post<DungeonMineResult>('/dungeon/mine', { depth }, act(actionId)),
  kill: (depth: number, mob: string, actionId?: string) =>
    api.post<DungeonKillResult>('/dungeon/kill', { depth, mob }, act(actionId)),
};

/** One crop's conversion rate, as the server publishes it. */
export interface SeedRate {
  cropId: string;
  name: string;
  kind: 'crop' | 'bush' | 'tree';
  produceItemId: string;
  seedItemId: string;
  /** How much produce buys ONE seed — always more than the crop's yield. */
  produceForOneSeed: number;
  yield: number;
  seedCost: number;
  unlockLevel: number;
  produceOwned: number;
  seedsOwned: number;
  /** How many seeds this bag could press right now. */
  canMake: number;
}
export interface SeedBoard { level: number; rates: SeedRate[] }
export interface SeedExtractResult {
  cropId: string; seedItemId: string; produceItemId: string;
  seeds: number; produceSpent: number; produceForOneSeed: number; bag: Bag;
}

export const fieldApi = {
  get: () => api.get<Farm>('/field'),
  crops: () => api.get<Crop[]>('/field/crops'),
  till: (tx: number, ty: number, actionId?: string) => api.post<Farm>('/field/till', { tx, ty }, act(actionId)),
  /** Hoe an EMPTY bed back to grass. Refused server-side while anything is planted. */
  clearPlot: (tx: number, ty: number, actionId?: string) => api.post<Farm>('/field/clear-plot', { tx, ty }, act(actionId)),
  plant: (tx: number, ty: number, cropId: string, actionId?: string) => api.post<Farm>('/field/plant', { tx, ty, cropId }, act(actionId)),
  water: (tx: number, ty: number, actionId?: string) => api.post<Farm>('/field/water', { tx, ty }, act(actionId)),
  harvest: (tx: number, ty: number, actionId?: string) => api.post<{ cropId: string; itemId: string; quantity: number; drops: GroundDrop[]; regrows: boolean; xp: number; level: number; leveledUp: boolean; farm: Farm }>('/field/harvest', { tx, ty }, act(actionId)),
  build: (kind: string, tx: number, ty: number, actionId?: string) => api.post<Farm>('/field/build', { kind, tx, ty }, act(actionId)),
  rushBuild: (tx: number, ty: number, actionId?: string) =>
    api.post<{ spent: number; farm: Farm }>('/field/rush-build', { tx, ty }, act(actionId)),
  rushCrop: (tx: number, ty: number, actionId?: string) =>
    api.post<{ spent: number; farm: Farm }>('/field/rush-crop', { tx, ty }, act(actionId)),
  unbuild: (tx: number, ty: number, actionId?: string) => api.post<Farm>('/field/unbuild', { tx, ty }, act(actionId)),
  /** Relocate a placed structure. Free of materials, and the build timer moves with it. */
  move: (fromTx: number, fromTy: number, tx: number, ty: number, actionId?: string) =>
    api.post<Farm>('/field/move', { fromTx, fromTy, tx, ty }, act(actionId)),
  bag: () => api.get<Bag>('/field/bag'),
  health: () => api.get<Health>('/field/health'),
  energy: () => api.get<EnergyStatus>('/field/energy'),
  rest: (actionId?: string) => api.post<EnergyStatus>('/field/rest', undefined, act(actionId)),
  sleep: (actionId?: string) => api.post<EnergyStatus & { slept: boolean }>('/field/sleep', undefined, act(actionId)),
  /**
   * Get up. `skip: true` pays Shrooms for the hours left and takes the full rest;
   * `false` gets up for free and forfeits it — the exit that exists so a player
   * with no Shrooms can never be stuck inside his own house.
   */
  wake: (skip: boolean, actionId?: string) =>
    api.post<EnergyStatus & { woke: boolean; skipped: boolean; spent: number }>('/field/wake', { skip }, act(actionId)),
  buyEnergyExtension: (actionId?: string) => api.post<EnergyStatus>('/field/energy/extension', undefined, act(actionId)),
  energyRefill: (actionId?: string) => api.post<EnergyStatus>('/field/energy/refill', undefined, act(actionId)),
  damage: (amount: number, actionId?: string) => api.post<Health & { died?: boolean }>('/field/damage', { amount }, act(actionId)),
  tent: (tx: number, ty: number, actionId?: string) => api.post<{ tx: number; ty: number; moved: boolean }>('/field/tent', { tx, ty }, act(actionId)),
  graves: () => api.get<Grave[]>('/field/graves'),
  claimGrave: (id: string, actionId?: string) => api.post<{ claimed: boolean; coins: number; resource: string | null; amount: number }>(`/field/graves/${id}/claim`, undefined, act(actionId)),
  /**
   * Seed-from-produce board: the server's rate per crop plus what you hold. The
   * ratio is NEVER computed here — the shop quotes what the server will charge.
   */
  seeds: () => api.get<SeedBoard>('/field/seeds'),
  /** Press `count` seeds out of the matching produce. */
  extractSeeds: (cropId: string, count: number, actionId?: string) =>
    api.post<SeedExtractResult>('/field/seeds/extract', { cropId, count }, act(actionId)),
  /** Burn one interval of a sustained cost (running / night cold). */
  burn: (reason: 'run' | 'cold', actionId?: string) =>
    api.post<{ spent: boolean; energy: number; maxEnergy: number }>('/field/burn', { reason }, act(actionId)),
};

// ─── Village ─────────────────────────────────────────────────────────────────
export type VillageResource = 'wood' | 'stone' | 'mushroom';
export interface VillagerType {
  kind: string; name: string; blurb: string;
  /**
   * null = a TASK hire (farmer / herder). They fill no farm column and their
   * `ratePerHour` is 0 — the panel must render them as labour, not production,
   * or they read as a producer that never produces anything.
   */
  produces: VillageResource | null;
  /** Building kind this villager can be assigned to work. */
  workplace: string;
  /** Cost of the NEXT summon — scales with the population you already have. */
  ratePerHour: number; costCoins: number; costWood: number; costStone: number;
}
/** A standing work order a villager carries out. Charges are derived on read. */
export interface VillagerTask {
  kind: string; name: string;
  charges: number; maxCharges: number;
  lastRunAt: string;
}
export interface TaskType {
  kind: string; name: string; blurb: string;
  /** Only this villager kind may hold it… */
  villagerKind: string;
  /** …and only while assigned to this workplace. */
  requiresJob: string;
  chargesPerHour: number; maxCharges: number;
}
export interface Villager {
  id: string; kind: string; name: string; tx: number; ty: number;
  produces: VillageResource | null; pending: number;
  /** The standing order it carries out, or null when it has none. */
  task: VillagerTask | null;
  /** Workplace building kind it works, or null when idle at home. */
  job: string | null; jobName: string | null;
  jobTx: number | null; jobTy: number | null;
  assignedAt: string | null;
  /** Effective units/hour with its current job applied. */
  ratePerHour: number;
}
/** A workplace building standing on the farm, and who (if anyone) works it. */
export interface VillageWorkplace {
  kind: string; name: string; tx: number; ty: number;
  /** Effective multiplier AT THIS BUILDING'S LEVEL, not the level-1 figure. */
  accepts: string[]; bonus: number;
  /** Upgrade level (1..maxLevel). Absent on older servers → treat as 1. */
  level?: number; maxLevel?: number;
  /** What the next level costs, or null/absent when it is already maxed. */
  upgradeCost?: { coins: number; wood: number; stone: number } | null;
  workerId: string | null; workerName: string | null;
}
export interface SettlementTier { id: string; name: string; minPop: number; unlocks?: string[] }
export interface VillageState {
  villagers: Villager[]; count: number; cap: number;
  workplaces: VillageWorkplace[];
  tier: SettlementTier; nextTier: SettlementTier | null; tiers: SettlementTier[];
  /** Building kind → population required to build it. */
  unlocks: Record<string, number>;
  types: VillagerType[]; resources: { wood: number; stone: number; mushroom: number };
  /** Static catalogue of the orders that exist (absent on older servers). */
  taskTypes?: TaskType[];
}

// ─── Villager tasks + husbandry ──────────────────────────────────────────────
/** One `tend_plots` run: the beds it actually watered. */
export interface TendPlotsRun {
  villagerId: string; kind: 'tend_plots';
  used: number; remaining: number;
  watered: { tx: number; ty: number }[];
  task: VillagerTask | null;
}
/** One `tend_animals` run: what it fed, what that cost, and what it banked. */
export interface TendAnimalsRun {
  villagerId: string; kind: 'tend_animals';
  used: number; remaining: number;
  fed: { id: string; species: string; fedUntil: string }[];
  consumed: Record<string, number>;
  collected: Record<string, number>;
  task: VillagerTask | null;
}
export type TaskRun = TendPlotsRun | TendAnimalsRun;

export interface FarmAnimal {
  id: string; species: string; name: string;
  penTx: number; penTy: number;
  produces: string; ratePerHour: number;
  pending: number; maxPending: number;
  fedUntil: string | null; hungry: boolean;
  feedItem: string; feedQty: number;
}
export interface Pen {
  kind: string; name: string; tx: number; ty: number;
  slots: number; used: number; accepts: string[]; animalIds: string[];
}
export interface LivestockType {
  species: string; name: string; pen: string; costCoins: number;
  produces: string; produceName: string; ratePerHour: number;
  feedItem: string; feedQty: number; fedHours: number;
}
export interface Husbandry {
  animals: FarmAnimal[];
  count: number; maxAnimals: number;
  pens: Pen[];
  species: LivestockType[];
  itemNames: Record<string, string>;
}
export interface FeedResult {
  fed: { id: string; species: string; fedUntil: string }[];
  consumed: Record<string, number>;
  collected: Record<string, number>;
  husbandry: Husbandry;
}

export interface FishStatus {
  hasRod: boolean; rodPrice: number; energyCostCast: number;
  energy: number; maxEnergy: number;
  species: { itemId: string; name: string; value: number }[];
}
export interface CastResult {
  caught: string | null; name: string | null; value?: number;
  xpEarned: number; level?: number; leveledUp?: boolean;
  energy: number; maxEnergy: number;
}

export const fishingApi = {
  get: () => api.get<FishStatus>('/fishing'),
  /** One cast. Serialised server-side, so a double tap costs one lot of energy. */
  cast: (actionId?: string) => api.post<CastResult>('/fishing/cast', undefined, act(actionId)),
  /** Buy the rod from Fin. */
  buyRod: (actionId?: string) => api.post<{ bought: string; price: number }>('/fishing/rod', undefined, act(actionId)),
};

export interface CompanionDef {
  id: string; name: string; blurb: string;
  currency: 'shrooms'; cost: number;
  damage: number; attackEveryMs: number; engageTiles: number; leashTiles: number;
}
export interface CompanionStatus {
  catalog: CompanionDef[]; owned: string[];
  /** Owned companions that can be sold back: price paid (`refund`), fee, and what lands (`net`). */
  sellBack?: { kind: string; refund: number; fee: number; net: number }[];
}
export interface CompanionSellBack { kind: string; name: string; paid: number; fee: number; net: number; status: CompanionStatus }

export const companionsApi = {
  get: () => api.get<CompanionStatus>('/companions'),
  /** Buy one. Serialised server-side, so a double tap cannot buy two. */
  buy: (kind: string, actionId?: string) =>
    api.post<CompanionStatus>('/companions/buy', { kind }, act(actionId)),
  /** Sell one back for the Shrooms paid, minus the sell-back fee. */
  sellBack: (kind: string, actionId?: string) =>
    api.post<CompanionSellBack>('/companions/sell-back', { kind }, act(actionId)),
};

export const villageApi = {
  get: () => api.get<VillageState>('/village'),
  summon: (kind: string, tx: number, ty: number, actionId?: string) =>
    api.post<VillageState>('/village/summon', { kind, tx, ty }, act(actionId)),
  /** Assign a villager to a workplace tile — omit tx/ty to send it home (idle). */
  assign: (villagerId: string, tx: number | null, ty: number | null, actionId?: string) =>
    api.post<{ banked: number; resource: string; village: VillageState }>(
      '/village/assign',
      tx === null || ty === null ? { villagerId } : { villagerId, tx, ty },
      act(actionId)),
  /** Improve a workplace: its worker produces faster and holds more between visits. */
  upgrade: (tx: number, ty: number, actionId?: string) =>
    api.post<{ level: number; maxLevel: number; banked: number; resource: string | null; village: VillageState }>(
      '/village/upgrade', { tx, ty }, act(actionId)),
  /** Send a villager away for good — frees the home so its House can be demolished. */
  dismiss: (villagerId: string, actionId?: string) =>
    api.post<{ dismissed: string; name: string; banked: number; resource: string | null; village: VillageState }>(
      '/village/dismiss', { villagerId }, act(actionId)),
  collect: (actionId?: string) =>
    api.post<{ granted: { wood: number; stone: number; mushroom: number }; village: VillageState }>('/village/collect', undefined, act(actionId)),

  /** Give (or, with kind null, clear) a villager's standing order. Charges restart at 0. */
  setTask: (villagerId: string, kind: string | null, actionId?: string) =>
    api.post<{ villagerId: string; task: VillagerTask | null }>('/village/task', { villagerId, kind }, act(actionId)),
  runTask: (villagerId: string, actionId?: string) =>
    api.post<TaskRun>('/village/task/run', { villagerId }, act(actionId)),
  runAllTasks: (actionId?: string) =>
    api.post<{ runs: TaskRun[] }>('/village/task/run-all', {}, act(actionId)),

  husbandry: () => api.get<Husbandry>('/village/husbandry'),
  buyAnimal: (species: string, tx: number, ty: number, actionId?: string) =>
    api.post<{ animalId: string; name: string; spent: number; husbandry: Husbandry }>(
      '/village/husbandry/buy', { species, tx, ty }, act(actionId)),
  /** Omit `animalIds` to feed everything the bag can pay for. */
  feedAnimals: (animalIds: string[] | undefined, actionId?: string) =>
    api.post<FeedResult>('/village/husbandry/feed', animalIds ? { animalIds } : {}, act(actionId)),
  collectProduce: (actionId?: string) =>
    api.post<{ granted: Record<string, number>; husbandry: Husbandry }>(
      '/village/husbandry/collect', {}, act(actionId)),
  releaseAnimal: (animalId: string, actionId?: string) =>
    api.post<{ released: string; banked: number; husbandry: Husbandry }>(
      '/village/husbandry/release', { animalId }, act(actionId)),
};

export interface Bag {
  /** wood/stone/ore/gem/mushroom plus `ore_<metal>` and `bar_<metal>` (see
   *  map/ores.ts) — hence the index signature rather than a closed list. */
  resources: { wood: number; stone: number; ore: number; gem: number; mushroom: number } & Record<string, number>;
  items: { itemId: string; quantity: number }[];
}

// ─── Forager Hut counter (trade goods ⇄ Coins) ───────────────────────────────
// Was the Market tab; it is now a building you walk to. `sell` is what the keeper
// PAYS per unit, `stock` is his shelf and its unit is what he CHARGES — the two
// are different numbers on purpose (the spread is what stops a buy/sell loop).
export interface TradeItem {
  itemId: string; name: string; unit: number; owned: number;
  /** Of `owned`, how many may be sold for Coins — the rest were bought with Shrooms. */
  coinSellable?: number;
}
/**
 * Goods bought with Shrooms that can go back for Shrooms: `units` of them,
 * `refund` = what those units cost, `fee` the sell-back commission on all of
 * them, `net` what the player receives for all of them.
 */
export interface SellBackRow { itemId: string; name: string; units: number; refund: number; fee: number; net: number }
/**
 * `shrooms` is the keeper's PREMIUM shelf (SHROOM_STOCK: ore, bars, the rare fish).
 * It was missing from this type and from the server's response, so nothing could
 * render it and `POST /forager/buy-shrooms` — which has existed, guarded and
 * tested, all along — was reachable from no screen in the game.
 */
export interface ForagerBoard {
  day: number; sell: TradeItem[]; stock: TradeItem[]; shrooms?: TradeItem[];
  sellBack?: SellBackRow[]; sellBackCommission?: number;
}
export interface SellBackResult { itemId: string; qty: number; gross: number; fee: number; net: number }
export interface TradeResult { itemId: string; qty: number; unit: number; total: number; coins: number }
export const foragerApi = {
  board: () => api.get<ForagerBoard>('/forager/board'),
  sell: (itemId: string, qty: number, actionId?: string) =>
    api.post<TradeResult>('/forager/sell', { itemId, qty }, act(actionId)),
  buy: (itemId: string, qty: number, actionId?: string) =>
    api.post<TradeResult>('/forager/buy', { itemId, qty }, act(actionId)),
  /** Sell goods bought with Shrooms back for Shrooms (price paid, minus the fee). */
  sellBack: (itemId: string, qty: number, actionId?: string) =>
    api.post<SellBackResult>('/forager/sell-back', { itemId, qty }, act(actionId)),
  /** Buy from the keeper's SHROOM shelf — priced per unit in Shrooms, never Coins. */
  buyShrooms: (itemId: string, qty: number, actionId?: string) =>
    api.post<TradeResult>('/forager/buy-shrooms', { itemId, qty }, act(actionId)),
  /**
   * Buy a seed for Coins. Its own route, not /buy: seeds are priced off the
   * crop's seedCost and gated on its unlock level, and they are deliberately not
   * sellable back (see forager.config SEED_STOCK).
   */
  buySeed: (itemId: string, qty: number, actionId?: string) =>
    api.post<{ itemId: string; cropId: string; qty: number; unit: number; total: number }>(
      '/forager/buy-seed', { itemId, qty }, act(actionId)),
  /** The Shroom catalogue (named bundles). Rows are a static price table, but
   *  `hasHut` is per-player, so this is never cached. */
  shroomItems: () => api.get<ShroomShelf>('/forager/shroom-items'),
  /**
   * Buy ONE catalogue row.
   *
   * NO QUANTITY ARGUMENT — the row IS the quantity (see ShroomItemDto: a smuggled
   * `qty` is a 400). `actionId` is not optional in spirit: this route is
   * idempotency-gated server-side because these rows are one-click and cost real
   * money, so a re-sent request must replay rather than charge twice.
   */
  buyShroomItem: (id: string, actionId?: string) =>
    api.post<ShroomItemPurchase>('/forager/buy-shroom-item', { id }, act(actionId)),
  /**
   * Buy the NEXT rung of one armour slot. Its own route, not a catalogue id:
   * armour is a tier on the farm, so nothing is granted into storage and the
   * server's guard is "is this the next rung" rather than "is this tradable".
   */
  buyArmour: (id: string, actionId?: string) =>
    api.post<ArmourPurchase>('/forager/buy-armour', { id }, act(actionId)),
};

// ─── Forager Shroom catalogue ─────────────────────────────────────────────────
// Bars and gems in fixed bundles, priced in Shrooms. Every number here is the
// SERVER'S (shroom-items.config): the shelf's whole economy rule is
// `shrooms * SHROOM_TO_COIN_RATE > quantity * coinValue`, and a price restated on
// the client is a second source of truth that drifts out of that check.
export interface ShroomItem {
  id: string; name: string; blurb: string;
  /** What lands in storage, and its display name. */
  itemId: string; itemName: string;
  /** How many units the bundle grants — NOT chosen by the caller. */
  quantity: number;
  /** Per-unit and bundle price, both in Shrooms. */
  unitShrooms: number; shrooms: number;
  /** Where the goods land, so the UI can say "farm store" vs "bag". */
  storage: 'farm' | 'bag';
}
/** `hasHut` rides along so a hutless player is told WHY the buttons 400. */
/**
 * One armour slot on the keeper's shelf: what you wear now, and the next rung he
 * will sell you. `offer` is null at the top of the ladder — nothing left to buy.
 */
export interface ArmourShelfRow {
  slot: 'helmet' | 'chest' | 'legs';
  currentTier: number;
  offer: { id: string; name: string; tier: number; shrooms: number } | null;
}
export interface ShroomShelf { hasHut: boolean; items: ShroomItem[]; armour?: ArmourShelfRow[] }
export interface ArmourPurchase {
  id: string; name: string; slot: string; tier: number;
  shrooms: number; shroomBalance: number;
}
export interface ShroomItemPurchase {
  id: string; name: string; itemId: string; itemName: string;
  quantity: number; unit: number; total: number;
  /** The premium balance AFTER the debit. */
  shroomBalance: number; currency: 'shrooms';
}

// ─── Timed boosters ──────────────────────────────────────────────────────────
export interface BuffCatalogItem {
  kind: string; name: string; blurb: string; mult: number; durationMs: number;
  currency: 'coins' | 'shrooms'; cost: number;
  /** Legacy field — 0 for Shroom-priced boosters. Prefer `currency` + `cost`. */
  costCoins: number;
}
/** Remaining ms per booster kind, keyed by kind (gather/xp/vigor/growth/luck/ward). */
export interface BuffStatus { active: Record<string, number>; catalog: BuffCatalogItem[] }
/** Energy/rest shelf in the shop (rest charge for Shrooms, refill for Coins). */
export interface EnergyOffer {
  id: 'rest_charge' | 'energy_refill';
  name: string; blurb: string; currency: 'coins' | 'shrooms'; cost: number; endpoint: string;
}
export const buffsApi = {
  get: () => api.get<BuffStatus>('/buffs'),
  buy: (kind: string, actionId?: string) => api.post<BuffStatus>('/buffs/buy', { kind }, act(actionId)),
};

// ─── Chat (world) ────────────────────────────────────────────────────────────
export interface ChatMessage {
  id: string; senderId: string; senderUsername: string | null; content: string; createdAt: string;
}
export const chatApi = {
  /** Recent world messages, oldest → newest. Live messages arrive over the socket. */
  history: (limit = 50) => api.get<ChatMessage[]>(`/chat/history?limit=${limit}`),
};

// ─── Shop / wallet ───────────────────────────────────────────────────────────
/** Every fiat figure the wallet reports, twice: the EUR accounting value and the
 *  same amount in the player's display currency. Both carry their code, so no
 *  screen has to infer euros from a bare integer. */
export interface MoneyPair { base: Money; display: Money }

export interface Wallet {
  /** Curve progress, computed server-side — never re-derived on the client. */
  xpIntoLevel?: number;
  xpForNextLevel?: number;
  xpToNextLevel?: number;
  isMaxLevel?: boolean;
  /** The player's display currency, with the rate it was served at. */
  currency: DisplayCurrency;
  /** Currency-tagged restatements of the bare Shroom counts below. */
  fiat: {
    balance: MoneyPair;
    refundable: MoneyPair;
    withdrawable: MoneyPair;
    lifetimeDeposited: MoneyPair;
    lifetimeWithdrawn: MoneyPair;
    depositAllowanceRemaining: MoneyPair;
    maxDeposit: MoneyPair;
    maxCustomDeposit: MoneyPair;
    minWithdraw: MoneyPair;
  };
  coins: number;
  level: number;
  xp: number;
  kycStatus: string;
  shrooms: number;
  refundableShrooms: number;
  withdrawable: number;
  lifetimeDeposited: number;
  lifetimeWithdrawn: number;
  depositAllowanceRemaining: number;
  limits: { maxDepositShrooms: number; minWithdrawShrooms: number; depositCommissionPct: number; withdrawCommissionPct: number;
    // Config-served so the shop never hardcodes the ceiling or the rate.
    minCustomDepositCents: number; maxCustomDepositCents: number; shroomsPerEur: number };
}
/** One of the player's own cash-out requests. */
export interface Withdrawal {
  id: string;
  amountShrooms: number;
  payoutCents: number;
  commissionShrooms: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'PAID';
  reason: string | null;
  createdAt: string;
  reviewedAt: string | null;
}
export interface WithdrawalState {
  enabled: boolean;
  kycRequired: boolean;
  items: Withdrawal[];
}

/** Identity verification, as the player submits it. */
export interface KycSubmission {
  fullName: string;
  /** ISO date, YYYY-MM-DD. */
  dateOfBirth: string;
  address: string;
  /** ISO 3166-1 alpha-2 — the endpoint takes exactly two letters. */
  country: string;
  document: File;
}

export interface Package { id: string; name: string; priceEur: number; shrooms: number; biome: string; rarity: string; art: string }
export interface PaymentMethod { id: string; label: string; description: string; active: boolean   /** Longer explanation shown in the method info popup. */
  detail?: string;
  /** Settlement currencies this method supports; absent = any. Server-filtered
   *  already — carried only so the UI can explain WHY a method is missing. */
  currencies?: string[];
}
export const shopApi = {
  packages: () => api.get<Package[]>('/shop/packages'),
  /**
   * Which methods can be used depends on the currency being charged — SEPA is a
   * euro instrument, for one — so the currency goes with the request and the
   * server decides. The client renders whatever comes back and filters nothing.
   */
  methods: (currency?: string) =>
    api.get<PaymentMethod[]>(`/shop/payment-methods${currency ? `?currency=${encodeURIComponent(currency)}` : ''}`),
  energyOffers: () => api.get<EnergyOffer[]>('/shop/energy-offers'),
  wallet: () => api.get<Wallet>('/shop/wallet'),
  /** The published currencies with today's rates — the picker reads this. */
  currencies: () => api.get<DisplayCurrency[]>('/shop/currencies'),
  /** `currency` is sent explicitly so the charge is taken in the currency the
   *  checkout quoted, even if the saved preference changed in another tab. */
  deposit: (packageId: string, method: string, currency?: string) =>
    api.post<Wallet>('/shop/deposit', { packageId, method, currency }),
  /** Custom top-up, in integer MINOR UNITS of `currency` (cents for EUR, yen for
   *  JPY) — see DepositCustomDto: money stays integral. */
  depositCustom: (amountCents: number, method: string, currency?: string) =>
    api.post<Wallet>('/shop/deposit/custom', { amountCents, method, currency }),
  /** Start a hosted card checkout. The server owns the price: a package is sent
   *  by id and priced from config, a custom top-up as `amountMinor` in the minor
   *  units of `currency`. The client never sends a price for a package.
   *  `method` is the picker's choice; the server re-checks it is offered for the
   *  settlement currency and defaults to card when it is omitted. */
  checkout: (item: { packageId?: string; amountMinor?: number; currency: string; method?: string }) =>
    api.post<{ id: string; url: string; provider: string }>('/shop/checkout', {
      ...item,
      screenWidth: window.screen?.width, screenHeight: window.screen?.height,
      timeZone: new Date().getTimezoneOffset(),
    }),
  exchange: (amountShrooms: number, actionId?: string) => api.post<Wallet>('/shop/exchange', { amountShrooms }, act(actionId)),
  /**
   * Cash-out state: whether the operator has opened withdrawals at all, whether
   * an identity check is required first, and this player's own request history.
   * Answers even when cash-out is closed — the screen has to be able to say so.
   */
  withdrawals: () => api.get<WithdrawalState>('/shop/withdrawals'),
  /**
   * Submit identity documents (core `POST /api/kyc/submit`, multipart).
   *
   * The Content-Type override is load-bearing: the axios instance defaults to
   * application/json, and with a JSON content-type axios serialises a FormData
   * body to JSON — silently dropping the file. Naming multipart here makes axios
   * pass the FormData through, and it then clears the header so the browser can
   * set the one with the boundary.
   */
  submitKyc: (s: KycSubmission) => {
    const form = new FormData();
    form.append('fullName', s.fullName);
    form.append('dateOfBirth', s.dateOfBirth);
    form.append('address', s.address);
    form.append('country', s.country);
    form.append('document', s.document);
    return api.post<{ kycStatus: string }>('/kyc/submit', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  /** File a cash-out request. The Shrooms are held until an admin decides. */
  withdraw: (params: {
    amountShrooms: number;
    payoutMethod: 'card' | 'iban';
    cardNumber?: string;
    iban?: string;
    bankNumber?: string;
    firstName: string;
    lastName: string;
  }, actionId?: string) =>
    api.post<Withdrawal>('/shop/withdraw', params, act(actionId)),
  healOffer: () => api.get<HealOffer>('/shop/heal-offer'),
  /** Buy the healing draught. actionId is what stops a double-tapped Confirm charging twice. */
  buyHeal: (actionId?: string) => api.post<Health & { spent: number; healed: number }>('/shop/heal', undefined, act(actionId)),
};

/** The paid heal (mirrors backend HEAL_OFFER) — quoted in the confirm popup. */
export interface HealOffer {
  id: 'heal_draught';
  name: string; blurb: string;
  currency: 'shrooms';
  cost: number; restoresHp: number;
}

/**
 * DEPRECATED — do not compute levels from this.
 *
 * The game's curve is geometric (base 100, x1.5, capped) and lives server-side in
 * progression/level.config. This flat constant was the old formula, and every
 * client that used it disagreed with the account screen the moment the curve
 * landed. Read `xpIntoLevel` / `xpForNextLevel` off the wallet or the profile
 * instead; they are computed once, on the server, from the real curve.
 */
export const XP_PER_LEVEL = 100;

/** 1 Shroom (Shroom) → this many Coins. Mirrors backend SHROOM_TO_COIN_RATE. */
export const SHROOM_TO_COIN_RATE = 10;

// ─── Woodcutting ─────────────────────────────────────────────────────────────
export interface WoodStatus {
  wood: number; pendingLogs: number; energy: number; maxEnergy: number;
  hitsPerTree: number; hitsPerStump: number; regrowMs: number;
  logsPerTree: number; logsPerStump: number; energyCostChop: number; axeTier: number;
  /** Acorn/replanting feel constants (absent on older servers). */
  acornChance?: number; energyCostPlantAcorn?: number; saplingGrowMs?: number;
}
export interface PlantAcornResult {
  energy: number; maxEnergy: number; sapling: Sapling;
}
export interface ChopResult {
  logsDropped: number; drops: GroundDrop[]; energy: number; maxEnergy: number;
  xpEarned: number; xp: number; level: number; leveledUp: boolean;
}
export interface PickupResult { wood: number; woodEarned: number; pendingLogs: number }
export const woodcuttingApi = {
  status: () => api.get<WoodStatus>('/woodcutting'),
  chop: (stump = false, at?: { tx: number; ty: number }, actionId?: string, size?: 'small' | 'big') =>
    api.post<ChopResult>('/woodcutting/chop', { stump, ...at, size }, act(actionId)),
  /** Push one acorn into the grass at a tile. The tile rules are the client's (it
   *  owns the map); the acorn, the energy and the growth clock are the server's. */
  plantAcorn: (tx: number, ty: number, actionId?: string) =>
    api.post<PlantAcornResult>('/woodcutting/plant-acorn', { tx, ty }, act(actionId)),
  saplings: () => api.get<{ saplings: Sapling[]; growMs: number }>('/woodcutting/saplings'),
};

// ─── Mining ──────────────────────────────────────────────────────────────────
export interface MineStatus {
  ore: number; gem: number; pendingOre: number; pendingGem: number;
  energy: number; maxEnergy: number; hitsPerRock: number; regrowMs: number;
  orePerRock: number; gemChance: number; energyCostMine: number; pickaxeTier: number;
  ores?: { type: string; name: string; color: string; pickaxeTier: number; raw: number; bars: number }[];
}
export interface MineResult {
  oreDropped: number; oreType: string | null; gemDropped: number; drops: GroundDrop[];
  energy: number; maxEnergy: number; xpEarned: number; xp: number; level: number; leveledUp: boolean;
}
export interface MinePickupResult { kind: 'ore' | 'gem'; ore: number; gem: number; earned: number; pendingOre: number; pendingGem: number }
export const miningApi = {
  status: () => api.get<MineStatus>('/mining'),
  // `oreType` names the vein's metal; the server re-derives the pickaxe gate and
  // the drop from it (requiredTier is the fallback for untyped rock).
  mine: (at?: { tx: number; ty: number }, requiredTier = 1, actionId?: string, oreType?: string) =>
    api.post<MineResult>('/mining/mine', { ...at, requiredTier, oreType }, act(actionId)),
};

// ─── Crafting (Block 7 — 6-tier ladder + stations) ───────────────────────────
export type CraftSlot = 'axe' | 'pickaxe' | 'hoe' | 'sword' | 'bow' | 'helmet' | 'chest' | 'legs';
export type StationKind = 'workbench' | 'furnace' | 'forge';
export interface CraftCost {
  wood?: number; stone?: number; ore?: number; gem?: number; coal?: number;
  barCopper?: number; barIron?: number; barGold?: number; barMythril?: number;
  // Tiers 7-9, matching backend crafting.config's Cost.
  barEmerald?: number; barAmber?: number; barRuby?: number;
}
// Every column a cost can name. Mirrors MATERIALS in backend crafting.config. The crafting panel iterates THIS
// to draw both the stock strip and every recipe's cost, so a metal missing here is
// invisible in the UI even though the server charges for it: emerald, amber and
// ruby costs simply did not appear on the tier 7-9 rows.
export const CRAFT_MATERIALS = ['wood', 'stone', 'ore', 'gem', 'coal',
  'barCopper', 'barIron', 'barGold', 'barMythril',
  'barEmerald', 'barAmber', 'barRuby'] as const;
export type CraftMaterial = (typeof CRAFT_MATERIALS)[number];
export interface CraftRecipe {
  id: string; slot: CraftSlot; kind: 'tool' | 'weapon' | 'armor'; tier: number; name: string;
  cost: CraftCost; station?: StationKind;
  isNext: boolean; owned: boolean; affordable: boolean; stationReady: boolean;
}
export interface CraftStation {
  kind: StationKind; name: string; cost: CraftCost; requires?: StationKind;
  built: boolean; prereqReady: boolean; affordable: boolean;
}
export interface CraftStatus {
  materials: Record<CraftMaterial, number>;
  tiers: Record<CraftSlot, number>;
  toolSlots: CraftSlot[]; armorSlots: CraftSlot[];
  stations: CraftStation[];
  recipes: CraftRecipe[];
}
export interface CraftResult { crafted: string; slot: CraftSlot; tier: number; status: CraftStatus }
export const craftingApi = {
  status: () => api.get<CraftStatus>('/crafting'),
  craft: (recipeId: string, actionId?: string) => api.post<CraftResult>('/crafting/craft', { recipeId }, act(actionId)),
  buildStation: (kind: string, actionId?: string) => api.post<{ built: string; status: CraftStatus }>('/crafting/station', { kind }, act(actionId)),
};

// ─── Smelting (the furnace: raw ore → bars, over time) ──────────────────────
export interface SmeltOre {
  type: string; name: string; color: string; tier: number; pickaxeTier: number;
  raw: number; bars: number; orePerBar: number; coalPerBar: number; msPerBar: number;
}
export interface SmeltJob {
  type: string; name: string; qty: number; collected: number; ready: number; pending: number;
  nextBarMs: number; remainingMs: number; perBarMs: number; complete: boolean;
}
export interface SmeltStatus {
  hasFurnace: boolean; unsortedOre: number; coal: number; maxBatch: number;
  ores: SmeltOre[]; job: SmeltJob | null;
}
export const smeltingApi = {
  status: () => api.get<SmeltStatus>('/smelting'),
  start: (type: string, bars: number, actionId?: string) =>
    api.post<{ started: string; bars: number; status: SmeltStatus }>('/smelting/start', { type, bars }, act(actionId)),
  collect: (actionId?: string) =>
    api.post<{ collected: number; type: string; bars: number; status: SmeltStatus }>('/smelting/collect', {}, act(actionId)),
  cancel: (actionId?: string) =>
    api.post<{ collected: number; refundedOre: number; type: string; status: SmeltStatus }>('/smelting/cancel', {}, act(actionId)),
};

// ─── Gathering ───────────────────────────────────────────────────────────────
/** Mirrors backend gathering.service GatherKind. */
export type GatherKind = 'stone' | 'mushroom' | 'bush' | 'grass' | 'flowers' | 'logs';
export interface GatherStatus {
  stone: number; mushroom: number; energy: number; maxEnergy: number;
  energyCostStone: number; energyCostMushroom: number;
  stoneRespawnMs: number; mushroomRespawnMs: number; bushRespawnMs?: number;
  energyCostFoliage?: number; foliageRespawnMs?: number;
  energyCostLogs?: number; logsRespawnMs?: number;
}
export interface GatherResult {
  kind: GatherKind; earned: number; drops: GroundDrop[];
  energy: number; maxEnergy: number; xpEarned: number; xp: number; level: number; leveledUp: boolean;
}
export const gatheringApi = {
  status: () => api.get<GatherStatus>('/gathering'),
  gather: (kind: GatherKind, at?: { tx: number; ty: number }, actionId?: string) => api.post<GatherResult>(`/gathering/${kind}`, { ...at }, act(actionId)),
};

// ─── Arena ───────────────────────────────────────────────────────────────────
// The old skill-duel arena (tiers / queue / pick-a-board matches) was REMOVED: its
// view was imported by nothing, and a 1v1 queue that needs a second live human is
// not a mode this game can fill — the battle royale below is bot-backed and is the
// arena that ships. Its endpoints went with it (see backend arena.controller.ts).

// ─── Arena battle royale (§11) ────────────────────────────────────────────────
export interface BrStatus {
  rating: number; wins: number; losses: number; matches: number;
  entriesToday: number; freeAvailable: boolean; nextEntryCostCoins: number; fieldSize: number;
  searchBanUntil: string | null; acceptWindowMs: number;   // §6.5 matchmaking
  coins: number;
  // Daily entry budget: 1 free · 10 base plays · up to 10 more bought with Shrooms.
  entriesLimit: number; entriesLeft: number; baseLimit: number;
  entriesBought: number; maxBoughtPerDay: number; boughtLeft: number;
  entryCostShrooms: number; shrooms: number;
}
export interface BrResult {
  placement: number; kills: number; goldBanked: number;
  ratingBefore: number; rating: number; ratingDelta: number; won: boolean;
}
export const arenaBrApi = {
  status: () => api.get<BrStatus>('/arena/br/status'),
  // Both mutations MUST carry the actionId: the server routes them through
  // ActionService, which can only dedup a replay when the header is present.
  // Without it a double-click was serialised rather than deduped, and the second
  // click was charged as a legitimate PAID entry — 100 coins silently lost.
  /**
   * Returns the `matchId` every result must be submitted against. The server writes
   * that row when it charges the entry, so it is the only proof a result belongs to
   * a match somebody actually paid for — carry it through the match and into
   * `result()` or the submit is refused.
   */
  enter: (seed: number, actionId?: string) =>
    api.post<{ seed: number; paid: number; matchId: string }>('/arena/br/enter', { seed }, act(actionId)),
  decline: () => api.post<{ searchBanUntil: string }>('/arena/br/decline', {}),
  /** Buy extra daily entries with Shrooms (max 10/day). */
  buyEntries: (count = 1, actionId?: string) =>
    api.post<BrStatus>('/arena/br/entries/buy', { count }, act(actionId)),
  /** `matchId` comes from enter(); without it the server has no entry to score against. */
  result: (r: { matchId: string; placement: number; kills: number; goldBanked: number }) => api.post<BrResult>('/arena/br/result', r),
};


// ─── Progression (quests + leaderboards) ─────────────────────────────────────
export interface QuestDefView { slug: string; name: string; description: string; questType: string; targetCount: number; rewardSoft: number; rewardXp: number }
export interface UserQuest { id: string; progress: number; status: string; quest: QuestDefView }
export interface LeaderEntry { rank: number; userId: string; username: string | null; value: number }
export interface OracleErrand {
  resource: string; resourceLabel: string; need: number; have: number;
  reward: number; flavor: string; ready: boolean; claimedToday: boolean;
}
export const progressionApi = {
  quests: () => api.get<UserQuest[]>('/progression/quests'),
  claim: (id: string, actionId?: string) => api.post<{ soft: number; premium: number; xp: number }>(`/progression/quests/${id}/claim`, undefined, act(actionId)),
  leaderboards: () => api.get<{ key: string; label: string }[]>('/progression/leaderboards'),
  oracle: () => api.get<OracleErrand>('/progression/oracle'),
  oracleClaim: (actionId?: string) => api.post<{ reward: number; resource: string; spent: number; coins: number }>('/progression/oracle/claim', undefined, act(actionId)),
};
// ─── Leaderboards tab ────────────────────────────────────────────────────────
export interface BoardDef { key: 'xp' | 'coins' | 'arena'; label: string; unit: string; blurb: string }
export interface BoardRow {
  rank: number; userId: string; username: string; level: number; value: number;
  /** Played within the last hour. */
  online: boolean;
  /** Places moved since the last hourly update (positive = up); null = new on the board. */
  moved: number | null;
  /** Change in value since the last hourly update. */
  gained: number;
  arena?: { wins: number; losses: number; matches: number; winRate: number };
}
export interface BoardPage {
  rows: BoardRow[];
  /** When the movement is measured from (the last hourly update); null before the first. */
  since: string | null;
  nextUpdate: string;
  me: { rank: number | null; value: number; total: number };
}
export const boardsApi = {
  list: () => api.get<BoardDef[]>('/progression/boards'),
  get: (key: string) => api.get<BoardPage>(`/progression/boards/${key}`),
};


// ─── Achievements ────────────────────────────────────────────────────────────
export interface Achievement {
  id: string;
  name: string;
  description: string;
  category: string;
  tier: 'bronze' | 'silver' | 'gold' | 'legendary';
  hidden: boolean;
  target: number;
  progress: number;
  rewardSoft: number;
  rewardXp: number;
  rewardPremium: number;
  unlocked: boolean;
  unlockedAt: string | null;
}
export interface AchievementsResult { unlockedCount: number; total: number; achievements: Achievement[] }
export const achievementsApi = {
  list: () => api.get<AchievementsResult>('/achievements'),
};

// ─── Account / settings ──────────────────────────────────────────────────────
export interface AccountProfile {
  /** Optional, player-supplied. The invoice is billed to the name when present. */
  personal?: PersonalDetails;
  id: string;
  email: string;
  username: string | null;
  emailVerified: boolean;
  role: string;
  memberSince: string;
  lastSeenAt: string | null;
  level: number;
  xp: number;
  xpIntoLevel: number;
  xpPerLevel: number;
  xpToNextLevel: number;
  isMaxLevel?: boolean;
  rankPoints: number;
  /** What fiat amounts should be shown in for this player, everywhere. */
  displayCurrency: DisplayCurrency;
  currencies: {
    coins: number; shrooms: number;
    refundableShrooms: number; lifetimeDeposited: number; lifetimeWithdrawn: number;
  };
  farm: { wood: number; ore: number; gem: number; stone: number; mushroom: number; energy: number; hp: number } | null;
}
export interface TransactionEntry {
  id: string;
  entryType: string;
  currency: 'SOFT' | 'PREMIUM';
  currencyLabel: 'Coins' | 'Shrooms';
  amount: number;
  balanceAfter: number;
  description: string;
  referenceId: string | null;
  createdAt: string;
  /** True only when a real-money Payment backs this row — the PDF invoice
   *  endpoint refuses everything else, so the client hides the action. */
  hasInvoice?: boolean;
  /**
   * The real-money side of the row, present only when a Payment backs it, and
   * FROZEN: `charged` is what the player actually paid at the rate that applied
   * then. Render it with formatFrozen() — converting `base` at today's rate
   * would restate a receipt the player already has.
   */
  fiat?: FrozenFiat | null;
}
export interface TransactionPage {
  items: TransactionEntry[];
  total: number; limit: number; offset: number; hasMore: boolean;
}

export interface PersonalDetails {
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  country: string | null;
  city: string | null;
  zipCode: string | null;
  stateCode: string | null;
  address: string | null;
}

/** A player's billing identity, as the admin panel reads and writes it. */
export interface AdminPlayerBilling {
  id: string;
  email: string;
  username: string | null;
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  zipCode: string;
  stateCode: string;
  country: string;
  phone: string;
}
export type AdminPlayerBillingInput = Partial<Omit<AdminPlayerBilling, 'id' | 'email' | 'username'>>;

/** How recently the player was around — derived from lastSeenAt on the server. */
export type PlayerActivity = 'online' | 'today' | 'this_week' | 'this_month' | 'dormant' | 'never';

/** One line of the card's recent activity (ledger or payment), newest first. */
export interface AdminActivityRow {
  id: string;
  /** LedgerEntryType, or DEPOSIT / DEPOSIT_REFUNDED for a fiat payment. */
  type: string;
  /** SHROOMS / COINS, or the ISO code of a fiat payment. */
  currency: string;
  /** Signed; fiat in MINOR units (cents) — format with fmtActivityAmount. */
  delta: number;
  description: string;
  createdAt: string;
}

/** The admin player card: one player in full. */
export interface AdminPlayerCard extends AdminPlayer {
  createdAt: string;
  /** Socket presence right now. */
  online: boolean;
  /** Unexpired refresh tokens — signed-in devices. */
  activeSessions: number;
  purchaseCount: number;
  withdrawalCount: number;
  recentActivity: AdminActivityRow[];
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  zipCode: string | null;
  stateCode: string | null;
  country: string | null;
  xp: number;
  maxLevel: number;
  role: string;
  emailVerified: boolean;
  kycStatus: string;
  isBot: boolean;
  createdByAdmin: boolean;
  invoiceEmailsSuppressed: boolean;
  heroPreset: string | null;
  heroAssignedAt: string | null;
  heroPresets: string[];
  activity: PlayerActivity;
  daysInGame: number;
  wallet: { refundableShrooms: number; lifetimeDeposited: number; lifetimeWithdrawn: number } | null;
  invoiceCount: number;
  billing: AdminPlayerBilling;
}

/** One save of the card. Omitted keys stay as they are. */
export interface AdminPlayerUpdate {
  nickname?: string;
  email?: string;
  level?: number;
  lastSeenAt?: string | null;
  heroPreset?: string | null;
  depositBlocked?: boolean;
  withdrawBlocked?: boolean;
  createdByAdmin?: boolean;
  emailVerified?: boolean;
  /** Profile & billing text; "" clears. */
  firstName?: string;
  lastName?: string;
  phone?: string;
  address?: string;
  city?: string;
  zipCode?: string;
  stateCode?: string;
  country?: string;
}

/** What the invoice form prefills once a player is named. */
export interface InvoiceBillingDefaults {
  userId: string;
  email: string;
  username: string | null;
  /** The player's own first / last name (prefill for the split name fields). */
  firstName?: string | null;
  lastName?: string | null;
  recipientName: string;
  recipientAddress: string;
  hasDetails: boolean;
  /** Admin-created player: invoices are never e-mailed. */
  emailSuppressed?: boolean;
}

export const accountApi = {
  profile: () => api.get<AccountProfile>('/account/profile'),
  /** The hero an operator applied from the admin player card, if any. */
  hero: () => api.get<{ heroPreset: string | null; heroAssignedAt: string | null }>('/account/hero'),
  /**
   * Save name / surname / phone. Every field is optional AND clearable: sending
   * an empty string wipes one, omitting a key leaves it alone. The server owns
   * both rules — see AccountService.updatePersonalDetails.
   */
  savePersonalDetails: (d: Partial<PersonalDetails>) =>
    api.patch<PersonalDetails & { personalCode: string | null }>('/account/personal-details', d),
  transactions: (q: { limit?: number; offset?: number; currency?: 'SOFT' | 'PREMIUM' } = {}) => {
    const p = new URLSearchParams();
    if (q.limit != null) p.set('limit', String(q.limit));
    if (q.offset != null) p.set('offset', String(q.offset));
    if (q.currency) p.set('currency', q.currency);
    const qs = p.toString();
    return api.get<TransactionPage>(`/account/transactions${qs ? `?${qs}` : ''}`);
  },
  /** The currencies an operator has published, for the settings picker. */
  currencies: () => api.get<DisplayCurrency[]>('/account/currencies'),
  /** The player's display currency (EUR unless he picked another). */
  currency: () => api.get<DisplayCurrency>('/account/currency'),
  /** Presentation only — the server moves no money when this changes. */
  setCurrency: (currency: string) => api.put<DisplayCurrency>('/account/currency', { currency }),
  /** Password change lives on the core auth module (it owns the credential). */
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ changed: boolean }>('/auth/change-password', { currentPassword, newPassword }),
  /**
   * PDFs come off authenticated endpoints, so they are fetched as a blob and
   * handed to the browser via an object URL — a bare <a download> would send no
   * credentials on some browsers and 401.
   */
  invoicePdf: (id: string) =>
    api.get<Blob>(`/account/transactions/${id}/invoice.pdf`, { responseType: 'blob' }),
  statementPdf: (range: { from?: string; to?: string } = {}) => {
    const p = new URLSearchParams();
    if (range.from) p.set('from', range.from);
    if (range.to) p.set('to', range.to);
    const qs = p.toString();
    return api.get<Blob>(`/account/statement.pdf${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
  },
};

// ─── Admin (ADMIN role only) ─────────────────────────────────────────────────
// Every endpoint here lives under /api/admin and is guarded server-side. The
// client only hides the tab; it never relies on that for security.
export interface AdminStats {
  totalPlayers: number;
  newPlayers: { today: number; week: number; month: number };
  onlineNow: number;
  battlesToday: number;
  circulatingGold: number;
  circulatingShrooms: number;
  recentPlayers: AdminPlayer[];
}
export interface AdminPlayer {
  id: string;
  /** The API sends both; `username` is canonical, `nickname` mirrors it. */
  username?: string | null;
  nickname: string | null;
  email: string;
  level: number;
  shrooms: number;
  coins: number;
  /** When the account was created. Named `joinedAt` by the admin API — reading
   *  `createdAt` here is what left the Joined column blank for every row. */
  joinedAt: string;
  kycVerified: boolean;
  banned: boolean;
  status?: string;
  lastSeenAt: string | null;
  depositBlocked: boolean;
  withdrawBlocked: boolean;
  /** Made in the admin panel / MAD — invoices are never e-mailed. */
  createdByAdmin: boolean;
}
export interface AdminTrendPoint {
  day: string; signups: number; battles: number; revenueCents: number; active: number;
}
export interface AdminTrends { days: number; points: AdminTrendPoint[] }

export interface AdminPlayerPage {
  items: AdminPlayer[];
  total: number; limit: number; offset: number;
}
export interface AdminPlayerQuery {
  query?: string;
  status?: 'all' | 'active' | 'banned' | 'kyc';
  limit?: number;
  offset?: number;
}
export interface AdminCreatePlayer {
  nickname: string; email: string; password: string;
  level?: number; shrooms?: number; kycVerified?: boolean;
}
export interface AdminPurchase {
  id: string;
  userId: string;
  /** The buyer. The API nests it; `nickname`/`email` are kept as optional flat
   *  aliases so either shape renders. */
  user?: { id: string; username: string | null; email: string } | null;
  nickname?: string | null;
  email?: string;
  shrooms: number;
  amountEur: number;
  status: string;
  provider: string | null;
  invoiceNumber: string | null;
  /** False when `invoiceNumber` is only the derived TB-xxxx fallback. */
  invoiceIssued?: boolean;
  /** Gateway payments: CREDITED | FAILED | REVIEW | PENDING; null for the simulated port. */
  fulfilmentStatus?: string | null;
  /** True when the credit failed or needs a human (amount/currency mismatch, untrusted rate). */
  needsReview?: boolean;
  fulfilmentNote?: string | null;
  createdAt: string;
}
export interface AdminPurchasePage {
  items: AdminPurchase[];
  total: number; limit: number; offset: number;
}
/**
 * One row of GET /admin/transactions — the player ledger (currency_ledger), merged with
 * the imported real-money rows (transaction_history, `source: 'import'`).
 */
export interface AdminTransaction {
  id: string;
  userId: string;
  /** Ledger entry type (CREDIT, DEBIT, ADMIN_GRANT, …) or an imported one (SHROOM_PURCHASE, …). */
  type: string;
  /** `SHROOMS` or `COINS`; an imported row's invoice currency (EUR, …), in cents. */
  currency: string;
  /** Signed amount in the currency's stored unit. */
  delta: number;
  /** null on an imported row (it moves no balance). */
  balanceAfter: number | null;
  source?: 'ledger' | 'import';
  /** `paid` / `not_paid` on an imported row, else null. */
  status?: string | null;
  description: string;
  referenceId: string | null;
  createdAt: string;
  user: { username: string | null; email: string } | null;
}
/** The server's real keys — `transactions`, not `items`/`rows`. */
export interface AdminTransactionPage {
  transactions: AdminTransaction[];
  total: number; page: number; pages: number; per_page: number;
}
export interface AdminTransactionQuery {
  page?: number; per_page?: number; type?: string; search?: string;
}
export interface AdminEconomy {
  faucets: { source: string; amount: number }[];
  sinks: { source: string; amount: number }[];
  float: number;
  deposits: number;
  commission: number;
  withdrawalLiability: number;
  circulatingGold: number;
  circulatingShrooms: number;
}
export interface AdminServerInfo {
  uptimeSec: number;
  env: string;
  version: string | null;
  db: { ok: boolean; latencyMs: number | null; name?: string | null };
  redis: { ok: boolean; latencyMs: number | null };
  queues: { name: string; waiting: number; active: number; failed: number }[];
}
// Field names mirror the Prisma models 1:1 (`content`, `sortOrder`) — the panel
// used to send `body`/`sort`, which the admin DTOs silently dropped, so every
// create came back "content is required".
export interface AdminNews {
  id: string; title: string; summary: string; content: string; tag: string;
  imageUrl: string | null; published: boolean; createdAt: string;
  /** Editable: the date the post claims, not the row's save stamp. */
  publishedAt: string | null;
}
export interface AdminFaq { id: string; question: string; answer: string; sortOrder: number }
export interface AdminLegal {
  id: string; slug: string; title: string; content: string; updatedAt: string;
  active: boolean;
  /** The "Last updated" date the public page shows; null = fall back to updatedAt. */
  effectiveDate: string | null;
}
export interface AdminSocial { id: string; platform: string; url: string; icon: string; sortOrder: number }
export interface AdminGuide {
  id: string; slug: string; title: string; summary: string; content: string;
  category: string; sortOrder: number; imageUrl: string | null;
  published: boolean; createdAt: string;
}
export interface AdminFooterSection {
  id: string; title: string; sortOrder: number; active: boolean;
}
export interface AdminFooterLink {
  id: string; sectionId: string; label: string; url: string; sortOrder: number; active: boolean;
}
export interface AdminApiKey {
  id: string; name: string; prefix: string; createdAt: string;
  lastUsedAt: string | null; revokedAt: string | null;
}
export interface AdminApiKeyCreated extends AdminApiKey { key: string }
export interface CompanyInfo {
  name: string; legalName: string; regNumber: string; vatNumber: string;
  address: string; country: string; email: string; phone: string; website: string;
}
export interface AdminSettings {
  commissionPercent: number;
  depositEnabled: boolean;
  sellEnabled: boolean;
  withdrawalEnabled: boolean;
  /** Require an approved identity check before a withdrawal is accepted. */
  kycEnabled: boolean;
  /** Issue a numbered OUT invoice when a withdrawal is approved. */
  invoicesEnabled: boolean;
  /** E-mail invoices to players (core `invoice.email_enabled`, default ON). Optional: older servers omit it. */
  invoiceEmailEnabled?: boolean;
  /**
   * Shrooms per euro, and the cash-out floor in euro.
   *
   * These two carry the BACKEND's spelling (`cryptePerEur`, `minWithdrawalEur`).
   * They used to be declared here as `crystalsPerEur`/`minWithdrawal`, which no
   * endpoint has ever sent or accepted: the panel therefore showed 0 for both and
   * silently dropped every edit to them.
   */
  cryptePerEur: number;
  minWithdrawalEur: number;
  company: CompanyInfo;
}

/** One payout request, as the admin queue lists it. */
export interface AdminWithdrawal {
  id: string;
  userId: string;
  amountShrooms: number;
  payoutCents: number;
  commissionShrooms: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'PAID';
  reason: string | null;
  createdAt: string;
  reviewedAt: string | null;
  invoiceNumber?: string | null;
}

/** One player in the identity-verification queue. */
export interface AdminKycUser {
  id: string;
  email: string;
  username: string | null;
  kycStatus: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED';
  kycFullName: string | null;
  kycCountry: string | null;
  kycSubmittedAt: string | null;
  kycReviewedAt: string | null;
}
export interface AdminKycPage { items: AdminKycUser[]; page: number; pageSize: number; total: number }

/** An invoice as the panel lists it. */
/** Field order of the import template and the preview table (server FIELDS, transaction-import/inputs.ts). */
export const IMPORT_FIELDS = [
  'invoiceNumber', 'email', 'username', 'amount', 'currency', 'type', 'status', 'purpose',
  'paymentMethod', 'maskedPan', 'paymentDetails', 'date', 'time', 'vatRate', 'shrooms', 'transactionId',
] as const;
export type ImportField = typeof IMPORT_FIELDS[number];

/** One row of an import report (POST /admin/invoicing/import[/rows]). */
export interface ImportItem {
  row: number;
  /** Normalised for editing; an unreadable or refused cell (a full card number) is null. */
  values: Record<ImportField, string | number | null>;
  errors: string[];
  preview: null | {
    username: string; newPlayer: boolean; usernameApplied: boolean; usernameTaken: boolean;
    invoiceNumber: string; shrooms: number; issuedAt: string; purpose: string;
  };
}

/** The import's report: a dry run, a refused commit (400, plus `error`) or a done one (plus `imported`…). */
export interface ImportReport {
  dryRun: boolean; valid: boolean; errors: string[]; totalErrors: number;
  rows: number; incoming: number; outgoing: number;
  playersMatched: number; playersToCreate: string[]; shroomsPerEur: number;
  items: ImportItem[];
  imported?: number;
  invoices?: string[];
  transactionIds?: string[];
  playersCreated?: { id: string; username: string | null; email: string }[];
}

/** A preview row as the panel sends it back: `row` + every field as typed. */
export type ImportRowBody = { row: number } & Record<ImportField, string>;

export interface AdminInvoice {
  id: string;
  number: string;
  type: 'IN' | 'OUT';
  status: string;
  amountCents: number;
  vatRate: number;
  vatCents: number;
  totalCents: number;
  /** The same amounts in major units (subtotal is excl. VAT). */
  subtotal?: number;
  vatAmount?: number;
  total?: number;
  currency: string;
  description: string;
  paymentMethod: string | null;
  transactionId?: string | null;
  itemCode?: string | null;
  /** Masked card number (first 6 + last 4 at most). */
  maskedPan?: string | null;
  /** Free-text payment details, printed as typed. */
  paymentDetails?: string | null;
  /** The imported transaction (transaction_history) this invoice records, else null. */
  transactionHistoryId?: string | null;
  /** First name when typed split (then `recipientSurname` holds the last name); else the full name. */
  recipientName: string | null;
  recipientSurname?: string | null;
  recipientCompany: string | null;
  recipientCode: string | null;
  recipientAddress: string | null;
  note: string | null;
  shrooms: number | null;
  paidAt: string | null;
  createdAt: string;
  /** The document date and time. */
  issuedAt: string;
  /** The player was created in the admin panel — this invoice is never e-mailed. */
  emailSuppressed: boolean;
  user: { id: string; email: string; username: string | null; createdByAdmin?: boolean } | null;
}
export interface AdminInvoicePage {
  items: AdminInvoice[]; total: number; limit: number; offset: number;
}
/** The form's payload — everything an operator chooses when raising one. */
export interface RaiseInvoice {
  userId: string;
  type: 'IN' | 'OUT';
  /** Excl. VAT, minor units. */
  amountCents: number;
  description: string;
  currency?: string;
  vatRate?: number;
  paymentMethod?: string;
  recipientName?: string;
  recipientCompany?: string;
  recipientCode?: string;
  /** Defaults to the player's stored billing address when omitted. */
  recipientAddress?: string;
  /** ISO date-time the document is dated. */
  issuedAt?: string;
  markPaid?: boolean;
  /** paid | pending | not_paid | cancelled | refunded. */
  status?: InvoiceStatusValue;
  recipientSurname?: string;
  transactionId?: string;
  itemCode?: string;
  /** Masked card number; the server refuses a full one. */
  maskedPan?: string;
  paymentDetails?: string;
  note?: string;
  /** Operator-chosen number; omitted → the next one off the counter. */
  number?: string;
  /** Shrooms the document covers — informational, never credited. */
  shrooms?: number;
}
/** Statuses an operator may set (the API's spelling; the list shows them upper-case). */
export type InvoiceStatusValue = 'paid' | 'pending' | 'not_paid' | 'cancelled' | 'refunded';
/** Edit an issued invoice. Omitted keys stay as they are. */
export interface UpdateInvoice {
  number?: string;
  /** Any case; a legacy stored value is sent back as stored. */
  status?: string;
  type?: 'IN' | 'OUT';
  transactionId?: string | null;
  itemCode?: string | null;
  recipientSurname?: string | null;
  /** '' / null clears. */
  maskedPan?: string | null;
  paymentDetails?: string | null;
  paymentMethod?: string | null;
  amountCents?: number;
  vatRate?: number;
  currency?: string;
  shrooms?: number | null;
  issuedAt?: string;
  description?: string;
  note?: string | null;
  recipientName?: string | null;
  recipientCompany?: string | null;
  recipientCode?: string | null;
  recipientAddress?: string | null;
}
/** One group of the invoice payment-method dropdown (plain-text methods). */
export interface InvoicePaymentMethodGroup { label: string; methods: string[] }
export interface InvoiceFormOptions {
  paymentMethods: InvoicePaymentMethodGroup[];
  currencies: string[];
  /** "EU currencies" / "Other currencies", as the dropdowns group them. */
  currencyGroups?: Array<{ label: string; currencies: string[] }>;
  nextNumber: string;
}

/** A competitive season and its reward ladder. */
export interface AdminSeasonTier {
  tierName: string; minPoints: number;
  rewardSoft: number; rewardPremium: number; rewardXp: number;
}
export interface AdminSeason {
  id: string;
  /** Sequence number the API assigns; shown next to the name. */
  number: number;
  name: string;
  status: 'UPCOMING' | 'ACTIVE' | 'ENDED' | string;
  startsAt: string;
  endsAt: string;
  /**
   * The reward ladder, as the API SPELLS IT: `rewards`, not `tiers`.
   *
   * It is `tiers` on the way IN (createSeason's body) and `rewards` on the way
   * out. Reading `tiers` off a response therefore always found undefined, and the
   * panel showed every season as having no reward tiers at all.
   */
  rewards?: AdminSeasonTier[];
}

/** The activity log's categories — MAD's six tabs (backend game/activity/activity-categories.ts). */
export const ACTIVITY_CATEGORIES = ['auth', 'sessions', 'user', 'finances', 'economy', 'game'] as const;
export type ActivityCategory = (typeof ACTIVITY_CATEGORIES)[number];

/** One row of GET /admin/activity and GET /admin/players/:id/activity. */
export interface AdminActivityEvent {
  id: string;
  userId: string;
  category: ActivityCategory;
  eventType: string;
  title: string;
  description: string;
  ipAddress: string | null;
  geo: string | null;
  createdById: string | null;
  eventAt: string;
  user: { username: string | null; email: string } | null;
}
/** The server's real keys — `events`, not `items`. */
export interface AdminActivityPage {
  events: AdminActivityEvent[];
  total: number; page: number; pages: number; per_page: number;
  categories: ActivityCategory[];
}
export interface AdminActivityQuery {
  page?: number; per_page?: number; category?: string; search?: string; player?: string;
}

/** Meta/SEO document: the stored rows plus the head the site would serve. */
export interface AdminSeo {
  values: Record<string, string>;
  resolved: Record<string, unknown> & { head?: string };
  editableKeys: string[];
  derivedKeys: string[];
}

/** A payment badge, as the operator toggles it. */
export interface AdminPaymentMethod {
  slug: string; name: string; icon: string; iconUrl?: string;
  enabled: boolean; sortOrder: number;
}

const qs = (o: Record<string, unknown>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v != null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const adminApi = {
  stats: () => api.get<AdminStats>('/admin/stats'),

  /** Daily series for the dashboard graphs. `days` clamped 1..90 server-side. */
  trends: (days = 30) => api.get<AdminTrends>(`/admin/trends?days=${days}`),
  players: (q: AdminPlayerQuery = {}) => api.get<AdminPlayerPage>(`/admin/players${qs(q as Record<string, unknown>)}`),
  createPlayer: (b: AdminCreatePlayer) => api.post<AdminPlayer>('/admin/players', b),
  giveShrooms: (id: string, amount: number, reason: string) =>
    api.post<AdminPlayer>(`/admin/players/${id}/shrooms`, { amount, reason }),
  setBanned: (id: string, banned: boolean, reason?: string) =>
    api.post<AdminPlayer>(`/admin/players/${id}/ban`, { banned, reason }),
  confirmKyc: (id: string, verified = true) =>
    api.post<AdminPlayer>(`/admin/players/${id}/kyc`, { verified }),
  /** Billing identity — its own request, never part of the list payload. */
  playerBilling: (id: string) => api.get<AdminPlayerBilling>(`/admin/players/${id}/billing`),
  savePlayerBilling: (id: string, b: AdminPlayerBillingInput) =>
    api.put<AdminPlayerBilling>(`/admin/players/${id}/billing`, b),
  /** The player card — one player in full. */
  playerCard: (id: string) => api.get<AdminPlayerCard>(`/admin/players/${id}`),
  /** Save the card's editable fields; omitted keys stay as they are. */
  updatePlayer: (id: string, b: AdminPlayerUpdate) => api.patch<AdminPlayerCard>(`/admin/players/${id}`, b),

  purchases: (q: { limit?: number; offset?: number; query?: string } = {}) =>
    api.get<AdminPurchasePage>(`/admin/purchases${qs(q as Record<string, unknown>)}`),
  /** Issue a numbered invoice for a purchase. Idempotent server-side. */
  generatePurchaseInvoice: (id: string) =>
    api.post<AdminPurchase>(`/admin/purchases/${id}/invoice`, {}),
  /** Re-run the Shroom credit for a FAILED / REVIEW gateway payment. Idempotent. */
  retryPurchaseCredit: (id: string) =>
    api.post<{ outcome: string; status: string | null; reason: string | null; lastError: string | null }>(`/admin/purchases/${id}/retry-credit`, {}),
  /** Authenticated PDF → blob, same pattern as accountApi.invoicePdf. */
  purchaseInvoicePdf: (id: string) =>
    api.get<Blob>(`/admin/purchases/${id}/invoice.pdf`, { responseType: 'blob' }),

  /** The player ledger, newest first. `type` and `search` as on the export. */
  transactions: (q: AdminTransactionQuery = {}) =>
    api.get<AdminTransactionPage>(`/admin/transactions${qs(q as Record<string, unknown>)}`),
  /** Every row matching the filter as transactions-export.xlsx (authenticated → blob). */
  exportTransactions: (q: { type?: string; search?: string } = {}) =>
    api.get<Blob>(`/admin/transactions/export${qs(q as Record<string, unknown>)}`, { responseType: 'blob' }),

  economy: () => api.get<AdminEconomy>('/admin/economy'),

  /** Cash-out queue. Approve realises the 4% commission (and issues the invoice). */
  withdrawals: (status?: string) =>
    api.get<AdminWithdrawal[]>(`/admin/shroom-withdrawals${qs({ status })}`),
  approveWithdrawal: (id: string) =>
    api.post<AdminWithdrawal>(`/admin/shroom-withdrawals/${id}/approve`, {}),
  rejectWithdrawal: (id: string, reason?: string) =>
    api.post<AdminWithdrawal>(`/admin/shroom-withdrawals/${id}/reject`, { reason }),

  /** Identity-verification queue (core KYC module). */
  kycUsers: (q: { status?: string; page?: number } = {}) =>
    api.get<AdminKycPage>(`/admin/kyc/users${qs(q as Record<string, unknown>)}`),
  approveKyc: (id: string) => api.post<{ success: boolean }>(`/admin/kyc/users/${id}/approve`, {}),
  rejectKyc: (id: string, reason: string) =>
    api.post<{ success: boolean }>(`/admin/kyc/users/${id}/reject`, { reason }),
  /** Core: marks the e-mail verified and drops pending verification links. */
  verifyEmail: (id: string) =>
    api.post<{ success: boolean; message?: string }>(`/admin/users/${id}/verify-email`, {}),

  /** Invoices raised from the panel (core's counter, panel's fields). */
  invoices: (q: { search?: string; type?: string; limit?: number; offset?: number } = {}) =>
    api.get<AdminInvoicePage>(`/admin/invoicing${qs(q as Record<string, unknown>)}`),
  invoicePaymentMethods: () =>
    api.get<InvoicePaymentMethodGroup[]>('/admin/invoicing/payment-methods'),
  /** Methods (text labels), EU currencies and the next invoice number. */
  invoiceFormOptions: () => api.get<InvoiceFormOptions>('/admin/invoicing/form-options'),
  raiseInvoice: (b: RaiseInvoice) => api.post<AdminInvoice>('/admin/invoicing', b),
  updateInvoice: (id: string, b: UpdateInvoice) => api.patch<AdminInvoice>(`/admin/invoicing/${id}`, b),
  /** The player's real name and address, to prefill the invoice form. */
  invoiceBillingDefaults: (userId: string) =>
    api.get<InvoiceBillingDefaults>(`/admin/invoicing/billing-defaults/${userId}`),
  /**
   * Core's invoice-only account (e-mail keyed; an existing e-mail returns that
   * account). Flagged admin-created on the server, so it is never e-mailed.
   */
  createUserForInvoice: (b: { email: string; username?: string }) =>
    api.post<{ id: string; email: string; username: string | null }>('/admin/users/create-for-invoice', b),
  setInvoicePaid: (id: string, paid: boolean) =>
    api.post<AdminInvoice>(`/admin/invoicing/${id}/paid`, { paid }),
  /** Authenticated PDF → blob, same pattern as the purchase invoice. */
  invoicePdf: (number: string) =>
    api.get<Blob>(`/admin/invoicing/${encodeURIComponent(number)}/pdf`, { responseType: 'blob' }),

  /** Transaction import: the .xlsx template (authenticated → blob). */
  importTemplate: () =>
    api.get<Blob>('/admin/invoicing/import/template', { responseType: 'blob' }),
  /**
   * Transaction import: an .xlsx / .csv checked by the server (dry run — nothing is written).
   * The multipart header is load-bearing (see submitKyc): with the client's JSON default,
   * axios would serialise the FormData to JSON and the server would answer "No file uploaded".
   */
  importPreviewFile: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<ImportReport>('/admin/invoicing/import?dryRun=true', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  /** Transaction import: the edited preview rows — re-check (dryRun) or import them. */
  importRows: (rows: ImportRowBody[], source: string, dryRun: boolean) =>
    api.post<ImportReport>(`/admin/invoicing/import/rows?dryRun=${dryRun}`, { source, rows }),

  /** Competitive seasons (core module) — list, create, activate, end. */
  seasons: () => api.get<AdminSeason[]>('/admin/seasons'),
  createSeason: (b: { name: string; startsAt: string; endsAt: string; tiers: AdminSeasonTier[] }) =>
    api.post<AdminSeason>('/admin/seasons', b),
  activateSeason: (id: string) => api.post<AdminSeason>(`/admin/seasons/${id}/activate`, {}),
  endSeason: (id: string) => api.post<AdminSeason>(`/admin/seasons/${id}/end`, {}),
  removeSeason: (id: string) => api.del<{ success?: boolean }>(`/admin/seasons/${id}`),

  /** The activity log across every player (category, search, player filters). */
  activity: (q: AdminActivityQuery = {}) =>
    api.get<AdminActivityPage>(`/admin/activity${qs(q as Record<string, unknown>)}`),
  /** One player's activity log — the player card's ACTIVITY LOG block. */
  playerActivity: (userId: string, q: AdminActivityQuery = {}) =>
    api.get<AdminActivityPage>(`/admin/players/${encodeURIComponent(userId)}/activity${qs(q as Record<string, unknown>)}`),

  /** Meta tags / SEO — the same `meta.*` rows the admin panel edits. */
  seo: () => api.get<AdminSeo>('/admin/seo'),
  saveSeo: (settings: Record<string, string>) => api.put<AdminSeo>('/admin/seo', { settings }),

  /** The accepted-payment badges shown in the footers. */
  paymentMethods: () => api.get<AdminPaymentMethod[]>('/admin/payment-methods'),
  togglePaymentMethod: (slug: string, enabled: boolean) =>
    api.post<{ ok: boolean; slug: string; enabled: boolean }>(
      '/admin/payment-methods/toggle', { slug, enabled }),
  bulkTogglePaymentMethods: (slugs: string[], enabled: boolean) =>
    api.post<{ ok: boolean; toggled: number; enabled: boolean }>(
      '/admin/payment-methods/bulk-toggle', { slugs, enabled }),
  server: () => api.get<AdminServerInfo>('/admin/server'),

  news: {
    list: () => api.get<{ items: AdminNews[] }>('/admin/cms/news').then((r) => r.items),
    create: (b: Partial<AdminNews>) => api.post<AdminNews>('/admin/cms/news', b),
    update: (id: string, b: Partial<AdminNews>) => api.put<AdminNews>(`/admin/cms/news/${id}`, b),
    remove: (id: string) => api.del<{ deleted: boolean }>(`/admin/cms/news/${id}`),
  },
  faq: {
    list: () => api.get<AdminFaq[]>('/admin/cms/faq'),
    create: (b: Partial<AdminFaq>) => api.post<AdminFaq>('/admin/cms/faq', b),
    update: (id: string, b: Partial<AdminFaq>) => api.put<AdminFaq>(`/admin/cms/faq/${id}`, b),
    remove: (id: string) => api.del<{ deleted: boolean }>(`/admin/cms/faq/${id}`),
  },
  legal: {
    list: () => api.get<AdminLegal[]>('/admin/cms/legal'),
    create: (b: Partial<AdminLegal>) => api.post<AdminLegal>('/admin/cms/legal', b),
    update: (id: string, b: Partial<AdminLegal>) => api.put<AdminLegal>(`/admin/cms/legal/${id}`, b),
    remove: (id: string) => api.del<{ deleted: boolean }>(`/admin/cms/legal/${id}`),
  },
  social: {
    list: () => api.get<AdminSocial[]>('/admin/cms/social'),
    create: (b: Partial<AdminSocial>) => api.post<AdminSocial>('/admin/cms/social', b),
    update: (id: string, b: Partial<AdminSocial>) => api.put<AdminSocial>(`/admin/cms/social/${id}`, b),
    remove: (id: string) => api.del<{ deleted: boolean }>(`/admin/cms/social/${id}`),
  },

  /** Guides — News' twin, ordered manually inside a category. */
  guides: {
    list: () => api.get<{ items: AdminGuide[] }>('/admin/cms/guides').then((r) => r.items),
    create: (b: Partial<AdminGuide>) => api.post<AdminGuide>('/admin/cms/guides', b),
    update: (id: string, b: Partial<AdminGuide>) => api.put<AdminGuide>(`/admin/cms/guides/${id}`, b),
    remove: (id: string) => api.del<{ success: boolean }>(`/admin/cms/guides/${id}`),
  },
  /** Footer columns. The list includes each column's links, which the link
   *  editor reuses to populate its section picker. */
  footerSections: {
    list: () => api.get<AdminFooterSection[]>('/admin/cms/footer-sections'),
    create: (b: Partial<AdminFooterSection>) => api.post<AdminFooterSection>('/admin/cms/footer-sections', b),
    update: (id: string, b: Partial<AdminFooterSection>) =>
      api.put<AdminFooterSection>(`/admin/cms/footer-sections/${id}`, b),
    remove: (id: string) => api.del<{ success: boolean }>(`/admin/cms/footer-sections/${id}`),
  },
  footerLinks: {
    list: () => api.get<AdminFooterLink[]>('/admin/cms/footer-links'),
    create: (b: Partial<AdminFooterLink>) => api.post<AdminFooterLink>('/admin/cms/footer-links', b),
    update: (id: string, b: Partial<AdminFooterLink>) =>
      api.put<AdminFooterLink>(`/admin/cms/footer-links/${id}`, b),
    remove: (id: string) => api.del<{ success: boolean }>(`/admin/cms/footer-links/${id}`),
  },

  apiKeys: {
    list: () => api.get<{ items: AdminApiKey[] }>('/admin/api-keys').then((r) => r.items),
    /** The plaintext key comes back exactly once, on create. */
    create: (name: string) => api.post<AdminApiKeyCreated>('/admin/api-keys', { name }),
    revoke: (id: string) => api.del<{ revoked: boolean }>(`/admin/api-keys/${id}`),
  },

  settings: () => api.get<AdminSettings>('/admin/settings'),
  saveSettings: (b: AdminSettings) => api.put<AdminSettings>('/admin/settings', b),
};

/** Public (unauthenticated) company details — used by the footer. */
export const publicApi = {
  company: () => api.get<CompanyInfo>('/public/company'),
};
